import * as signalR from "@microsoft/signalr";
import { storageService } from "./storageService";
import {
    hasValidSession,
    isAccessTokenExpired,
    refreshAccessToken,
    setOnAccessTokenRefreshed,
} from "./tokenRefreshService";

const baseURL = window.__env.apiUrl;
const URL = baseURL + '/signalr-hub';
const FIRST_ATTEMPT_DELAY = 10000;

type ReconnectedHandler = (connectionId?: string) => void;

class SignalrService {
    private connection: signalR.HubConnection;
    private reconnectedHandlers = new Set<ReconnectedHandler>();
    private connectionLost = false;
    private startRetryCount = 0;
    private startRetryTimeout: ReturnType<typeof setTimeout> | null = null;
    private readonly retryPolicy = new CustomRetryPolicy();
    static instance: SignalrService;

    constructor() {
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(URL, {
                accessTokenFactory: () => this.getAccessToken(),
            })
            .withAutomaticReconnect(this.retryPolicy)
            .configureLogging(signalR.LogLevel.Error)
            .build();

        this.handleConnectionEvents();
        window.addEventListener('online', this.handleOnline);
        setOnAccessTokenRefreshed(() => this.onAccessTokenRefreshed());
        this.ensureConnected();
    }

    public subscribeEvent = (methodName: string, handler: (args: any) => void) => {
        this.connection.on(methodName, handler);
    };

    public unsubscribeEvent = (methodName: string, handler: (args: any) => void) => {
        this.connection.off(methodName, handler);
    };

    public onReconnected = (handler: ReconnectedHandler) => {
        this.reconnectedHandlers.add(handler);
    };

    public offReconnected = (handler: ReconnectedHandler) => {
        this.reconnectedHandlers.delete(handler);
    };

    public ensureConnected = () => {
        if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
            return;
        }

        this.connection.start()
            .then(() => {
                console.log(this.connectionLost ? 'WS reconnected' : 'WS connected');
                this.startRetryCount = 0;
                if (this.startRetryTimeout) {
                    clearTimeout(this.startRetryTimeout);
                    this.startRetryTimeout = null;
                }
                if (this.connectionLost) {
                    this.notifyReconnectedHandlers();
                }
            })
            .catch(err => {
                console.error("Error while establishing WS connection", err);
                this.connectionLost = true;
                this.scheduleStartRetry();
            });
    };

    private onAccessTokenRefreshed = () => {
        if (this.connection.state === signalR.HubConnectionState.Disconnected) {
            this.ensureConnected();
        }
    };

    private getAccessToken = async (): Promise<string> => {
        if (isAccessTokenExpired() && hasValidSession()) {
            try {
                await refreshAccessToken();
            } catch (e) {
                console.error("Failed to refresh access token for SignalR", e);
            }
        }

        return storageService.getTokenData()?.accessToken || "";
    };

    private handleOnline = () => {
        this.ensureConnected();
    };

    private notifyReconnectedHandlers() {
        this.connectionLost = false;
        const connectionId = this.connection.connectionId ?? undefined;
        this.reconnectedHandlers.forEach(handler => handler(connectionId));
    }

    private scheduleStartRetry() {
        if (this.startRetryTimeout) {
            return;
        }

        const delay = getRetryDelayMs(this.startRetryCount);
        console.log(`Attempt number ${this.startRetryCount + 1}, retrying in ${delay} milliseconds`);
        this.startRetryCount++;

        this.startRetryTimeout = setTimeout(() => {
            this.startRetryTimeout = null;
            this.ensureConnected();
        }, delay);
    }

    private handleConnectionEvents() {
        this.connection.onreconnecting(error => {
            console.warn(`WS reconnecting: ${error}`);
            this.connectionLost = true;
        });

        this.connection.onreconnected(connectionId => {
            console.log(`WS reconnected: ${connectionId}`);
            this.startRetryCount = 0;
            this.notifyReconnectedHandlers();
        });

        this.connection.onclose(error => {
            console.error(`WS connection closed: ${error}`);
            this.connectionLost = true;
            this.scheduleStartRetry();
        });
    }

    public static getInstance(): SignalrService {
        if (!SignalrService.instance)
            SignalrService.instance = new SignalrService();
        return SignalrService.instance;
    }
}
export default SignalrService.getInstance;

class CustomRetryPolicy implements signalR.IRetryPolicy {
    nextRetryDelayInMilliseconds(retryContext: signalR.RetryContext) {
        const delay = getRetryDelayMs(retryContext.previousRetryCount);
        console.log(`Attempt number ${retryContext.previousRetryCount + 1}, retrying in ${delay} milliseconds`);
        return delay;
    }
}

function getRetryDelayMs(previousRetryCount: number): number {
    return (previousRetryCount + 1) * FIRST_ATTEMPT_DELAY;
}
